import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import { useTranslation } from 'react-i18next';
import type { TutorWeeklyWorkingHours } from '../../models/Tutor';
import {
  TUTOR_WEEKDAYS,
  formatTutorWorkingHoursDay,
  getTutorWeekdayLabel,
  hasTutorWorkingHoursSchedule,
  normalizeTutorTimezone,
  normalizeTutorWorkingHours,
} from '../../utils/tutorWorkingHours';

export interface TutorWorkingHoursSummaryProps {
  workingHours?: TutorWeeklyWorkingHours | null;
  timezone?: string | null;
  /** Hides the section header when nested inside another titled card. */
  hideHeader?: boolean;
}

const TutorWorkingHoursSummary: React.FC<TutorWorkingHoursSummaryProps> = ({
  workingHours,
  timezone,
  hideHeader = false,
}) => {
  const { t } = useTranslation();
  const normalizedWorkingHours = useMemo(
    () => normalizeTutorWorkingHours(workingHours),
    [workingHours],
  );
  const normalizedTimezone = normalizeTutorTimezone(timezone);
  const hasSchedule = hasTutorWorkingHoursSchedule(normalizedWorkingHours);

  if (!hasSchedule && !normalizedTimezone) {
    return null;
  }

  return (
    <Stack spacing={0.75}>
      {!hideHeader ? (
        <Stack direction="row" spacing={0.55} alignItems="center" useFlexGap flexWrap="wrap">
          <AccessTimeRoundedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
          <Typography variant="overline" color="text.secondary">
            {t('tutor.workingHours.title', 'Working hours')}
          </Typography>
          {normalizedTimezone ? (
            <Stack direction="row" spacing={0.35} alignItems="center">
              <PublicRoundedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {normalizedTimezone}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      ) : normalizedTimezone ? (
        <Typography variant="caption" color="text.secondary">
          {normalizedTimezone}
        </Typography>
      ) : null}

      {hasSchedule ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            columnGap: 1.5,
            rowGap: 0.25,
          }}
        >
          {TUTOR_WEEKDAYS.map((weekday) => (
            <Stack
              key={weekday}
              direction="row"
              spacing={1}
              justifyContent="space-between"
              alignItems="baseline"
            >
              <Typography variant="caption" color="text.secondary">
                {getTutorWeekdayLabel(weekday, t)}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {formatTutorWorkingHoursDay(normalizedWorkingHours?.[weekday], t)}
              </Typography>
            </Stack>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('tutor.workingHours.notConfigured', 'No working hours configured yet.')}
        </Typography>
      )}
    </Stack>
  );
};

export default TutorWorkingHoursSummary;
